<script setup>
import apiManager from '@/api';
import {
  ref,
  reactive,
  computed,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import pages from './pages';
import AppointmentModalPure from './AppointmentModalPure.vue';
import statusCodes from './statusCodes';

const store = useStore();
const serverErrorMessage = ref();

const props = defineProps({
  event: Object,
  sessionId: String, // for reschedule, cancel
  initialPage: {
    type: String,
    default: pages.sendCode,
  },
});

const emit = defineEmits(['close', 'clear-session-id']);

// const appointmentsAvailable = ref(false);
const appointmentsAvailable = computed(() => {
  const appointments = props.event?.configSME?.appointments;

  if (!appointments) {
    return false;
  }

  if (appointments.every((appt) => appt.available === 0)) {
    return false;
  }

  return true;
});

const statuses = reactive({
  sendCodeStatus: statusCodes.default,
  signInStatus: statusCodes.default,
  scheduleStatus: statusCodes.default,
  cancelStatus: statusCodes.default,
});

const currentPage = ref(props.initialPage);

const email = ref('');
const verification = ref('');

const sendCode = async (_email) => {
  try {
    statuses.sendCodeStatus = statusCodes.loading;
    await apiManager.email.sendConfirmationEmail(_email);
    statuses.sendCodeStatus = statusCodes.success;
    email.value = _email;
    currentPage.value = pages.signIn;
  } catch (e) {
    statuses.sendCodeStatus = statusCodes.failed;
  }
};

const signIn = async (code) => {
  try {
    statuses.signInStatus = statusCodes.loading;
    await apiManager.email.confirmEmail({ email: email.value, code });
    await store.dispatch('events/loadEvent', { id: props.event.id, email: email.value });
    statuses.signInStatus = statusCodes.success;
    verification.value = code;
    currentPage.value = pages.appointmentSchedule;
  } catch (e) {
    statuses.signInStatus = statusCodes.failed;
  }
};

const sendAgain = () => {
  currentPage.value = pages.sendCode;
};

/* --- for populating AppointmentSchedule.vue in the case of rescheduling --- */
const session = ref();
const isExpiredLink = ref(false);

const preLoadSession = async (id) => {
  let response;
  try {
    response = await apiManager.appointments.getSession(id);
    if (response.status === 200) {
      session.value = response.data;
      email.value = response.data.user.email;
    }
  } catch (e) {
    console.error('Session may no longer exist, restarting sme appointment flow.');

    isExpiredLink.value = true;

    emit('clear-session-id');
    nextTick(() => {
      currentPage.value = pages.sendCode;
    });
  }
};

if (props.sessionId) {
  preLoadSession(props.sessionId);
}
/* --- end --- */
const user = ref();

const schedule = async (inputs) => {
  const userInputs = { ...inputs.user, email: email.value };
  statuses.scheduleStatus = statusCodes.loading;

  try {
    if (props.sessionId) {
      await apiManager.appointments.deleteSession(props.sessionId);
    }
    await apiManager.appointments.submitSession({
      appointmentId: inputs.appointmentId,
      comment: inputs.comment,
      inputs: userInputs,
    });

    user.value = userInputs;
    statuses.scheduleStatus = statusCodes.success;
    currentPage.value = pages.scheduleConfirmed;

    await store.dispatch('events/loadEvent', { id: props.event.id }); // reload event after update
  } catch (e) {
    const error = JSON.parse(JSON.stringify(e));
    if (error.status === 409) {
      serverErrorMessage.value = 'The appointment session you selected is no longer available. Please select a different appointment session.';
    }
    statuses.scheduleStatus = statusCodes.failed;
  }
};

const reschedule = () => {
  currentPage.value = pages.appointmentSchedule;
};

const cancel = async () => {
  try {
    statuses.cancelStatus = statusCodes.loading;
    await apiManager.appointments.deleteSession(props.sessionId);
    statuses.scheduleStatus = statusCodes.success;
    currentPage.value = pages.cancelConfirmed;
  } catch (e) {
    statuses.scheduleStatus = statusCodes.failed;
  }
};

const close = () => emit('close');
</script>
<template>
  <appointment-modal-pure
    @send-code="sendCode"
    @sign-in="signIn"
    @send-again="sendAgain"
    @schedule="schedule"
    @reschedule="reschedule"
    @cancel="cancel"
    @close="close"
    :statuses="statuses"
    :currentPage="currentPage"
    :event="event"
    :appointments-available="appointmentsAvailable"
    :email="email"
    :verification="verification"
    :user="user"
    :session="session"
    :is-expired-link="isExpiredLink"
    :server-error-message="serverErrorMessage"
  />
</template>
